import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { EmailDomainSchema } from '@/lib/types';
import { Input } from '@reflex/ui/components/ui/input';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';

export interface EmailDomainsFieldProps {
  /** Current domain list. Caller owns persistence. */
  value: string[];
  /** Receives the updated domain list (lower-cased, deduped, validated). */
  onChange: (domains: string[]) => void;
  /** Disable input and chip removal (e.g. while saving). */
  disabled?: boolean;
  /** Placeholder for the new-domain input. */
  placeholder?: string;
  /** Test-id prefix; e.g. `org-email-domains` produces `*-input` and `*-chip-<domain>`. */
  testIdPrefix?: string;
  /** Extra className for the outer container. */
  className?: string;
}

/**
 * Compact chip-input for editing a list of email domains. Reused by
 * org create/edit forms and the admin auth allowlist editor.
 */
export function EmailDomainsField({
  value,
  onChange,
  disabled = false,
  placeholder = 'e.g. runloop.ai',
  testIdPrefix = 'email-domains',
  className,
}: EmailDomainsFieldProps) {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  function tryAdd(): void {
    const candidate = draft.trim();
    if (candidate.length === 0) return;
    const parsed = EmailDomainSchema.safeParse(candidate);
    if (!parsed.success) {
      setDraftError(parsed.error.issues[0]?.message ?? 'Invalid domain');
      return;
    }
    if (value.includes(parsed.data)) {
      setDraftError(`"${parsed.data}" is already in the list`);
      return;
    }
    onChange([...value, parsed.data]);
    setDraft('');
    setDraftError(null);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAdd();
    } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  function handleRemove(domain: string): void {
    onChange(value.filter((d) => d !== domain));
  }

  return (
    <div className={cn('space-y-2', className)}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {value.map((domain) => (
            <Badge
              key={domain}
              variant="outline"
              className="gap-1.5 bg-muted/40 py-1 pl-2.5 pr-1 leading-none"
              data-testid={`${testIdPrefix}-chip-${domain}`}
            >
              <span className="font-mono">{domain}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => handleRemove(domain)}
                aria-label={`Remove ${domain}`}
                className="-mr-0.5 rounded-full"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (draftError) setDraftError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label="Add email domain"
            aria-invalid={draftError ? true : undefined}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            data-testid={`${testIdPrefix}-input`}
          />
          {draftError && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {draftError}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={tryAdd}
          disabled={draft.trim().length === 0 || disabled}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
